import { CommandPalette } from "@/features/command-palette";
import { featureFlags } from "@/lib/env";
import {
  Outlet,
  type ParsedLocation,
  createRootRouteWithContext,
  redirect,
} from "@tanstack/react-router";
import { GlobalConfirmDialog } from "../components/Dialog/GlobalConfirmDialog/GlobalConfirmDialog";
import { FullScreenOverlay } from "../components/Loading";
import { NotFound } from "../components/NotFound/NotFound";
import { useAuthStore } from "../lib/auth";
import useChangeLanguageByKeyPress from "../lib/i18n/hooks/useChangeLanguageByKeyPress";
import { useGlobalLoadingStore } from "../lib/store/loading";
import type { queryClient } from "../lib/queryClient";
type AuthCheckResult = {
  isAuthenticated: boolean;
};

/**
 * ここでは、Cognitoの認証チェックを行い、未認証の場合はログアウトする
 * また、APIを呼び出した場合に401エラーが走った場合は、ログアウトする
 * -> src/apps/app/src/lib/api/auth-middleware.ts
 */
async function performAuthCheck(): Promise<AuthCheckResult> {
  const store = useAuthStore.getState();
  try {
    const isAuthenticated = await store.isAuthenticated();
    if (isAuthenticated) {
      // NOTE: refreshTokenを取得してsessionを継続するために以下を実行している
      await store.getAccessToken();
      await store.getSession();
    }
    return { isAuthenticated };
  } catch (error) {
    console.warn("Authentication check failed:", error);
    store.logout();
    return { isAuthenticated: false };
  }
}

async function checkAuthentication(
  location: ParsedLocation<Record<string, never>>,
) {
  const isUnauthenticatedRoot = location.pathname.startsWith("/auth");
  if (isUnauthenticatedRoot) {
    return;
  }

  const { isAuthenticated } = await performAuthCheck();
  if (!isAuthenticated) {
    throw redirect({
      to: "/auth/sign-in",
      search: {
        redirect: location.pathname,
      },
      replace: true,
    });
  }
}

export interface RouterContext {
  queryClient: typeof queryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: Root,
  beforeLoad: async ({ location }) => {
    await checkAuthentication(location);
  },
  notFoundComponent: NotFound,
});

function Root() {
  const { isLoading, message } = useGlobalLoadingStore();

  // 開発者ショートカットとしてcontrol + command + lで日英をトグルする
  useChangeLanguageByKeyPress();

  return (
    <>
      <GlobalConfirmDialog />

      {/* 開発者用のショートカット。Cmd+Kでコマンドパレットを開く。 */}
      {featureFlags.check("NewFeature") && <CommandPalette />}

      {/* 組織切り替えなどのグローバルな状態変更時はFullScreenOverlayを使用してユーザー操作をブロック */}
      {isLoading && <FullScreenOverlay message={message} />}
      <Outlet />
    </>
  );
}
